import { Box, Card, CardActions, CardContent, IconButton, Typography } from "@mui/material";
import { useState } from "react";
import Grid from "@mui/material/Grid2";
import { BootstrapTooltip } from "components/PWATabs/mainTab/CommentsTable";
import { ICreativeGroupItem } from "interfaces/creatives";
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import showPreviewIcon from "utils/showPreviewImage";
import { LazyImage } from "components/LazyImage";
import imgPlaceholder from "assets/imageApp.png";
import useCautionStore from "store/cautionStore";
import TouchAppIcon from "@mui/icons-material/TouchApp";

interface ICreativesListComponentProps {
  creativesArr: ICreativeGroupItem[];
  setIsModalOpen: (value: boolean) => void;
  setCreativesGroupForEdit: (group: ICreativeGroupItem) => void;
}

const CreativesList = ({ creativesArr, setIsModalOpen, setCreativesGroupForEdit }: ICreativesListComponentProps) => {
  const { t } = useTranslation();
  const { handleCreativeToDeleteChange, openCreativesGroupDeleteModal } = useCautionStore();
  const [hoveredCreativesIndex, setHoveredCreativesIndex] = useState<number | null>(null);

  const handleDeleteModalOpen = (creativeGroup: ICreativeGroupItem) => {
    handleCreativeToDeleteChange(creativeGroup);
    openCreativesGroupDeleteModal();
  };

  const handleEditCreativeModalOpen = (creativeGroup: ICreativeGroupItem) => {
    setIsModalOpen(true);
    setCreativesGroupForEdit(creativeGroup);
  };

  return (
    <Grid container spacing={2} sx={{ padding: "2px" }}>
      {creativesArr.length === 0 ? (
        <Grid size={12} sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <Typography variant="subtitle2" fontWeight={400}>
            {t("noitems")}
          </Typography>
        </Grid>
      ) : (
        creativesArr.map((createve, index) => {
          const { _id, group_name, group_type, images, isAnimated, animated_images } = createve;
          const showAnimatedCreatives = isAnimated && hoveredCreativesIndex === index;
          const iconSrc = showAnimatedCreatives ? animated_images.icon : images.icon;
          const previewsSrc = showAnimatedCreatives ? animated_images.previews : images.previews;

          return (
            <Grid key={_id} size={{ xs: 12, md: 6, xl: 4 }}>
              <Card
                onMouseEnter={() => setHoveredCreativesIndex(index)}
                onMouseLeave={() => setHoveredCreativesIndex(null)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  borderRadius: "8px",
                  boxShadow: "0 0 4px 0 rgba(165, 163, 174, 0.3)",
                  cursor: isAnimated ? "pointer" : "auto",
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    alignItems: "flex-start",
                    flex: "1 1 auto",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                    <Box sx={{ display: "flex", gap: "16px", width: "100%" }}>
                      <img
                        src={showPreviewIcon(iconSrc)}
                        alt="pwa-icon"
                        style={{
                          width: 76,
                          height: 76,
                          borderRadius: "8px",
                        }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: "var(--cygreRegular), sans-serif",
                            fontSize: "16px",
                            fontWeight: 500,
                            color: "#2E263D",
                          }}
                        >
                          {t(`${group_name}`)}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "var(--cygreRegular), sans-serif",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#7c7c81",
                          }}
                        >
                          {`(${t(`${group_type}`)})`}
                        </Typography>
                      </Box>
                    </Box>
                    {isAnimated && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          height: "fit-content",
                          padding: "4px 8px",
                          border: "1px solid #EEEEEE",
                          borderRadius: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            paddingTop: "4px",
                            fontFamily: "var(--cygreMedium), sans-serif",
                            color: "#2E263D",
                            fontSize: "12px",
                            lineHeight: 1.22,
                            textAlign: "left",
                          }}
                        >
                          {t("animated")}
                        </Typography>

                        <TouchAppIcon sx={{ width: "22px", height: "22px", color: "#F4701B" }} />
                      </Box>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      flexWrap: "nowrap",
                      gap: "4px",
                      width: "100%",
                      paddingBottom: "10px",
                      overflowX: "auto",
                      flex: "1 1 auto",
                    }}
                  >
                    {previewsSrc.map((preview: any, index: number) => {
                      return (
                        <LazyImage
                          key={index}
                          customStyles={{
                            objectFit: "contain",
                            maxHeight: "150px",
                            borderRadius: "4px",
                          }}
                          src={showPreviewIcon(preview)}
                          placeholder={imgPlaceholder}
                          alt="preview"
                        />
                      );
                    })}
                  </Box>
                </CardContent>
                <CardActions
                  sx={{
                    justifyContent: "flex-end",
                    backgroundColor: "#f3f2f2",
                    borderTop: "1px solid rgba(165, 163, 174, 0.3)",
                    mt: "auto",
                  }}
                >
                  <BootstrapTooltip title={t("edit")} placement="top">
                    <IconButton aria-label="edit-icon" onClick={() => handleEditCreativeModalOpen(createve)}>
                      <EditIcon sx={{ width: "20px", height: "20px", color: "#6D6777" }} />
                    </IconButton>
                  </BootstrapTooltip>
                  <BootstrapTooltip title={t("delete")} placement="top">
                    <IconButton aria-label="delete-icon" onClick={() => handleDeleteModalOpen(createve)}>
                      <DeleteIcon sx={{ width: "20px", height: "20px", color: "#6D6777" }} />
                    </IconButton>
                  </BootstrapTooltip>
                </CardActions>
              </Card>
            </Grid>
          );
        })
      )}
    </Grid>
  );
};

export default CreativesList;
